/**
 * Channel ↔ JSON-RPC method mapping — TDD-v2 §2.3 (mapping tables).
 *
 * The IPC layer uses `<namespace>:<verb>` channel names (e.g.
 * `agent:send`, `game:import:pick-directory`). The cloud-VM JSON-RPC
 * channel uses `<namespace>.<verb>@<version>` (per
 * `docs/cloud-vm/design/jsonrpc-channel.md` §4 — e.g.
 * `agent.send@1`, `game.import.pick-directory@1`). This module
 * codifies the deterministic transform between the two.
 *
 * Why a deterministic transform rather than a hand-rolled table?
 * `InvokeChannelMap` has ~200 entries and `PushChannelMap` has ~30.
 * The convention is consistent (`:` → `.`, append `@1`), so the
 * one-line transform stays in lockstep with new channels without a
 * generation step. If the convention ever needs per-channel overrides
 * (versioned methods, namespace renames), this module switches to a
 * code-generated table without touching call sites.
 *
 * Cross-references:
 *   - jsonrpc-channel.md §4 — naming convention
 *   - jsonrpc-channel.md §5 — Phase 0c minimum method set
 *   - TDD-v2 §2.3 — `BrowserCrucibleClient` invoke / notification routing
 */

import type {
  InvokeChannelMap,
  PushChannelMap,
} from '@volley/crucible-ipc-types';

const JSONRPC_VERSION_SUFFIX = '@1';

/**
 * Translate an IPC invoke channel to its JSON-RPC method name.
 *
 *   channelToMethod('agent:send')  → 'agent.send@1'
 *   channelToMethod('game:create') → 'game.create@1'
 */
export function channelToMethod<C extends keyof InvokeChannelMap>(
  channel: C,
): string {
  return channel.replace(/:/g, '.') + JSONRPC_VERSION_SUFFIX;
}

/**
 * Translate an IPC push channel to its JSON-RPC notification method
 * name. Push channels follow the same convention.
 *
 *   pushToMethod('agent:stream') → 'agent.stream@1'
 */
export function pushToMethod<E extends keyof PushChannelMap>(
  push: E,
): string {
  return push.replace(/:/g, '.') + JSONRPC_VERSION_SUFFIX;
}

/**
 * Reverse direction: a JSON-RPC method name back to its push-channel
 * key for fan-out via `subscribe()`. Returns `null` when the method
 * isn't shaped like a push (no version suffix, unrecognised
 * namespace) so the caller can swallow it as an unknown notification
 * rather than crashing.
 *
 *   methodToPush('agent.stream@1') → 'agent:stream'
 *   methodToPush('request.cancel@1') → null  (system method, not a push)
 *
 * Note: this function does NOT validate the result against
 * `PushChannelMap`'s keys at runtime — the type assertion narrows it
 * for the caller, who relies on the subscriber set being empty for
 * unknown channels (handler list is keyed off the same map, so an
 * unrecognised channel has no subscribers and the dispatch is a
 * no-op). Round-trip pin: `pushToMethod(methodToPush(m)!) === m`
 * holds for every push method.
 */
export function methodToPush(method: string): keyof PushChannelMap | null {
  if (!method.endsWith(JSONRPC_VERSION_SUFFIX)) return null;
  const stripped = method.slice(0, -JSONRPC_VERSION_SUFFIX.length);
  if (stripped.length === 0) return null;
  return stripped.replace(/\./g, ':') as keyof PushChannelMap;
}
